/**
 * Сетка пресетов цвета — как в macOS/Office, вместо сырого RGB-пикера.
 *
 * Решение founder 08.08: `input type=color` открывает системный диалог с
 * полями R/G/B и HSB. Агроному там делать нечего, а на выходе получаются
 * цвета, которые на карте плохо соседствуют. Здесь — готовая сетка: клик =
 * выбрал.
 *
 * Примитив НЕ знает про домен (ревью head-web): палитра, значение «нет цвета»
 * и ширина ряда приходят пропами. Иначе следующий потребитель со своей
 * палитрой был бы вынужден форкнуть компонент — а это ровно тот путь, каким
 * заводятся вторые Button'ы.
 *
 * Два РАЗНЫХ действия, которые нельзя склеивать (требование design-lead):
 *   · «Нет цвета» — осознанный выбор человека «не выделять запись на карте»;
 *     это ЗНАЧЕНИЕ (`noColorValue`), а не отсутствие выбора.
 *   · «Вернуть общий цвет» — стереть своё переопределение и снова слушать
 *     общий справочник. Показывается, только когда переопределение есть.
 * В macOS они бы слиплись в один «Automatic», и тогда «сделать серым» было бы
 * невозможно отличить от «вернуть как было».
 *
 * Доступность: сетка — одна roving-tabindex группа. `tabIndex=0` считается ОТ
 * VALUE, а не от фокуса: активный свотч, а если активного нет (цвет пришёл из
 * «Другие цвета…» или из БД до появления палитры) — первый. Без этого fallback
 * группа выпадала из Tab-порядка целиком и с клавиатуры в неё было не войти
 * (дефект нашёл head-web).
 */
import { useRef } from 'react';
import { Check, Pipette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ColorSwatchPickerProps {
  /** Текущий цвет (`#RRGGBB`) или `null`, если переопределения нет. */
  value: string | null;
  onChange: (color: string) => void;
  /** Палитра пресетов. Домен передаёт свою — примитив её не выбирает. */
  swatches: readonly string[];
  /** Свотчей в ряду; от него же шаг стрелок ↑/↓. */
  perRow?: number;
  /**
   * Значение «нет цвета» (нейтральный). Не передано — кнопка не рисуется:
   * у справочника без нейтрали такого выбора просто нет.
   */
  noColorValue?: string;
  /**
   * Вернуть общий цвет (стереть переопределение). Не передан — кнопка не
   * рисуется: у записей без общего эталона возвращать нечего.
   */
  onReset?: () => void;
  /** Цвет, который получится после сброса — показываем образцом на кнопке. */
  inheritedColor?: string | null;
  disabled?: boolean;
  /**
   * `id` внешней подписи. `<Label htmlFor>` на `div` не работает (метка
   * ссылается на не-форменный элемент) — связываем через `aria-labelledby`
   * на самой группе.
   */
  labelledBy?: string;
}

export function ColorSwatchPicker({
  value,
  onChange,
  swatches,
  perRow = 10,
  noColorValue,
  onReset,
  inheritedColor,
  disabled = false,
  labelledBy,
}: ColorSwatchPickerProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);

  const normalized = value?.toUpperCase() ?? null;
  const activeIndex = swatches.findIndex((c) => c.toUpperCase() === normalized);
  /**
   * Куда встаёт Tab. Активный свотч, иначе первый — «иначе» покрывает и
   * «переопределения нет», и «цвет есть, но он не из палитры». Второй случай
   * и ронял вход с клавиатуры: `tabIndex=0` не доставался никому.
   */
  const tabStop = activeIndex >= 0 ? activeIndex : 0;

  /** Стрелки водят фокус по сетке — как по одному контролу, а не по 25 табам. */
  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const delta =
      e.key === 'ArrowRight'
        ? 1
        : e.key === 'ArrowLeft'
          ? -1
          : e.key === 'ArrowDown'
            ? perRow
            : e.key === 'ArrowUp'
              ? -perRow
              : 0;
    if (delta === 0) return;
    const next = index + delta;
    if (next < 0 || next >= swatches.length) return;
    e.preventDefault();
    const buttons = gridRef.current?.querySelectorAll<HTMLButtonElement>('[data-swatch]');
    buttons?.[next]?.focus();
  }

  /**
   * Системный пикер: `showPicker()` открывает его без «фантомного» клика по
   * скрытому инпуту. Метод есть не везде (Safari < 16.4) — там остаётся
   * `.click()`, который в Safari как раз и работает.
   */
  function openNativePicker() {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // showPicker бросает вне user-gesture — падать на этом нельзя.
      }
    }
    el.click();
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={gridRef}
        role="group"
        aria-label={labelledBy ? undefined : 'Цвет записи'}
        aria-labelledby={labelledBy}
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
      >
        {swatches.map((c, i) => {
          const active = normalized === c.toUpperCase();
          return (
            <button
              key={c}
              type="button"
              data-swatch
              title={c}
              aria-label={c}
              aria-pressed={active}
              disabled={disabled}
              tabIndex={i === tabStop ? 0 : -1}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onClick={() => onChange(c)}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md border border-border/60',
                'transition-transform focus-visible:outline-none focus-visible:ring-2',
                'focus-visible:ring-ring focus-visible:ring-offset-2',
                active && 'ring-2 ring-ring ring-offset-2',
                !disabled && 'hover:scale-110',
                disabled && 'cursor-not-allowed opacity-50',
              )}
              style={{ backgroundColor: c }}
            >
              {active && <Check className="h-3 w-3 text-white drop-shadow" />}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* «Нет цвета» — ЗНАЧЕНИЕ, а не сброс: запись остаётся нейтральной. */}
        {noColorValue && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            aria-pressed={normalized === noColorValue.toUpperCase()}
            onClick={() => onChange(noColorValue)}
          >
            <span
              aria-hidden
              className="mr-2 inline-block h-3 w-3 rounded-sm border border-border"
              style={{ backgroundColor: noColorValue }}
            />
            Нет цвета
          </Button>
        )}

        {/* Сброс переопределения — отдельное действие, не «Нет цвета». */}
        {onReset && value !== null && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onReset}
          >
            {inheritedColor && (
              <span
                aria-hidden
                className="mr-2 inline-block h-3 w-3 rounded-sm border border-border"
                style={{ backgroundColor: inheritedColor }}
              />
            )}
            Вернуть общий цвет
          </Button>
        )}

        {/* Fallback для «нужен ровно наш корпоративный оттенок». */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={openNativePicker}
        >
          <Pipette className="mr-2 h-3 w-3" />
          Другие цвета…
        </Button>
        <input
          ref={nativeRef}
          type="color"
          tabIndex={-1}
          aria-hidden
          className="sr-only"
          value={normalized ?? noColorValue ?? '#000000'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      </div>
    </div>
  );
}

export default ColorSwatchPicker;
